/**
 * Shown after a tool is selected from the menu, ribbon, palette, or library.
 * Pick the volume / case to work on, then stay on the tool page.
 */

import * as React from "react";

import { Button } from "@/components/ui/primitives";
import { DialogFooter, DialogOverlay, DialogPanel, DialogTitle, dialogPrimaryButtonClass, dialogSecondaryButtonClass } from "@/components/ui/dialog";
import { listRecentCases, openCase, splitError } from "@/ipc/api";
import type { CaseNode, RecentCase } from "@/ipc/contract";
import { formatDateTime, cn } from "@/lib/utils";
import { pickAndOpenSubject, showCaseInViewer } from "@/pipeline/actions";
import { appActions, useCurrentCase, useSelectedNodeId } from "@/store/appStore";
import { toolById } from "@/tools/catalog";
import { useToolSession } from "@/tools/session";
import { useInteractiveSegStore } from "@/tools/segment/store";
import { useStageRunStore } from "@/tools/stageRun/store";
import { useViewerStore } from "@/viewer/viewerStore";

export function ToolLaunchDialog() {
  const open = useToolSession((s) => s.launchOpen);
  const closeLaunch = useToolSession((s) => s.closeLaunch);
  const selectedId = useToolSession((s) => s.selectedId);
  const currentCase = useCurrentCase();
  const selectedNodeId = useSelectedNodeId();
  const [recent, setRecent] = React.useState<RecentCase[]>([]);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const tool = toolById(selectedId ?? "");
  const volumes: CaseNode[] = currentCase ? [...currentCase.images, ...currentCase.masks] : [];
  const currentVolume = volumes.find((n) => n.id === selectedNodeId) ?? volumes[0] ?? null;

  const refreshRecent = React.useCallback(() => {
    void listRecentCases()
      .then(setRecent)
      .catch(() => setRecent([]));
  }, []);

  React.useEffect(() => {
    if (!open) return;
    setError(null);
    refreshRecent();
  }, [open, refreshRecent, currentCase?.caseId]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeLaunch();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closeLaunch]);

  if (!open || !tool) return null;

  const selectVolume = (node: CaseNode) => {
    appActions.setSelectedNodeId(node.id);
    useViewerStore.getState().setBaseNode(node.id);
  };

  const openRecent = async (item: RecentCase) => {
    setBusy(true);
    setError(null);
    try {
      await showCaseInViewer(await openCase(item.caseJsonPath), { tab: tool.tab });
      refreshRecent();
    } catch (err) {
      setError(splitError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const browse = async () => {
    setBusy(true);
    setError(null);
    try {
      await pickAndOpenSubject({ tab: tool.tab });
      refreshRecent();
    } catch (err) {
      setError(splitError(err).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogOverlay
      align="top"
      className="z-[75]"
      onMouseDown={() => {
        if (!busy) closeLaunch();
      }}
    >
      <DialogPanel className="max-h-[76vh] w-[520px]" onMouseDown={(event) => event.stopPropagation()}>
        <DialogTitle title={tool.label} subtitle={tool.description} />

        <div className="min-h-0 flex-1 space-y-5 overflow-auto px-6 pb-2">
          <section>
            <h3 className="mb-1.5 text-[10.5px] font-semibold tracking-wide text-ads-muted uppercase">
              Current volume
            </h3>
            {currentVolume ? (
              <button
                type="button"
                className="w-full rounded-xl border border-ads-accent-border bg-ads-accent-soft px-3.5 py-2.5 text-left transition-colors hover:bg-ads-accent-soft/80"
                onClick={() => selectVolume(currentVolume)}
              >
                <span className="block text-[12.5px] text-ads-text">{currentVolume.label}</span>
                <span className="block text-[10.5px] text-ads-muted">{currentVolume.origin}</span>
              </button>
            ) : (
              <p className="px-1 py-2 text-[12px] text-ads-muted">
                No case open. Pick a recent case or browse a subject folder.
              </p>
            )}
          </section>

          <section>
            <h3 className="mb-1.5 text-[10.5px] font-semibold tracking-wide text-ads-muted uppercase">
              Case volumes
            </h3>
            {volumes.length === 0 ? (
              <p className="px-1 py-1 text-[12px] text-ads-muted">No volumes in this case.</p>
            ) : (
              <ul className="overflow-hidden rounded-xl bg-black/[0.025]">
                {volumes.map((node) => {
                  const active = node.id === currentVolume?.id;
                  return (
                    <li key={node.id}>
                      <button
                        type="button"
                        className={cn(
                          "flex w-full items-center justify-between px-3.5 py-2 text-left text-[12px] transition-colors",
                          active ? "bg-ads-accent-soft text-ads-text" : "text-ads-text hover:bg-black/[0.03]",
                        )}
                        onClick={() => selectVolume(node)}
                      >
                        <span>{node.label}</span>
                        <span className="text-[10.5px] text-ads-muted">{node.origin}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section>
            <h3 className="mb-1.5 text-[10.5px] font-semibold tracking-wide text-ads-muted uppercase">
              Recent cases
            </h3>
            {recent.length === 0 ? (
              <p className="px-1 py-1 text-[12px] text-ads-muted">No recent cases.</p>
            ) : (
              <ul className="overflow-hidden rounded-xl bg-black/[0.025]">
                {recent.map((item) => (
                  <li key={item.caseId}>
                    <button
                      type="button"
                      disabled={busy}
                      className="flex w-full items-center justify-between px-3.5 py-2 text-left transition-colors hover:bg-black/[0.03] disabled:opacity-50"
                      onClick={() => void openRecent(item)}
                    >
                      <span>
                        <span className="block text-[12px] text-ads-text">{item.subjectId}</span>
                        <span className="block text-[10.5px] text-ads-muted">
                          {item.studyName || item.modality}
                        </span>
                      </span>
                      <span className="text-[10.5px] text-ads-muted">
                        {formatDateTime(item.lastModifiedIso)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {error ? <p className="text-[12px] text-red-700">{error}</p> : null}
        </div>

        <DialogFooter className="justify-between">
          <Button type="button" size="sm" className={dialogSecondaryButtonClass} disabled={busy} onClick={() => void browse()}>
            Browse subject folder…
          </Button>
          <div className="flex gap-2">
            <Button type="button" size="sm" className={dialogSecondaryButtonClass} disabled={busy} onClick={closeLaunch}>
              Cancel
            </Button>
            <Button type="button" size="sm" variant="primary" className={dialogPrimaryButtonClass} disabled={busy} onClick={() => {
              // DICOM import creates a volume rather than operating on one
              // already in the case, so it is the one tool that does not
              // need a volume picked first — `StageRunPanel` shows its own
              // file picker instead of loading `currentVolume`.
              const skipVolumePick = tool.itkTool === "dicomImport";
              if (!currentVolume && !skipVolumePick) {
                setError("Pick a volume first.");
                return;
              }
              if (currentVolume) selectVolume(currentVolume);
              closeLaunch();
              if (tool.interactive && currentVolume) {
                useInteractiveSegStore.getState().openPanel(tool.id, currentVolume.id);
                return;
              }
              // Every other tool opens its own run panel here instead of
              // closing and leaving the reader wherever they happened to be
              // — `StageRunPanel` either runs it for real (brain extraction
              // and the itk-wasm tools today) or says plainly why it cannot
              // yet, but it never just does nothing.
              useStageRunStore.getState().openPanel(tool.id, currentVolume?.id ?? "");
            }}>
              Continue
            </Button>
          </div>
        </DialogFooter>
      </DialogPanel>
    </DialogOverlay>
  );
}
